import { useI18n } from '@/lib/i18n'
import type { EventConfig, TriggerRule } from '@/types'
import { TRIGGER_TYPES, TRIGGER_ACTIONS } from '@/lib/constants'
import { generateId } from '@/lib/utils'

export default function TriggersSettings({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const triggers = event.triggers

  const addTrigger = () => {
    const rule: TriggerRule = {
      id: generateId(),
      type: 'button',
      action: 'start',
      delay: 0,
      config: {},
    }
    onChange({ triggers: [...triggers, rule] })
  }

  const updateTrigger = (id: string, updates: Partial<TriggerRule>) => {
    onChange({ triggers: triggers.map((r) => (r.id === id ? { ...r, ...updates } : r)) })
  }

  const deleteTrigger = (id: string) => {
    onChange({ triggers: triggers.filter((r) => r.id !== id) })
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h2 className="text-xl font-semibold">{t('editor.triggers')}</h2>
      <button onClick={addTrigger} className="rounded-md bg-secondary px-4 py-2 text-sm font-medium hover:bg-secondary/80">{t('triggers.add')}</button>

      <div className="space-y-3">
        {triggers.map((trigger) => (
          <div key={trigger.id} className="grid grid-cols-5 gap-3 rounded-lg border border-border p-4 items-end">
            <div>
              <label className="text-xs text-muted-foreground">{t('triggers.type')}</label>
              <select value={trigger.type} onChange={(e) => updateTrigger(trigger.id, { type: e.target.value as TriggerRule['type'] })} className="w-full rounded border bg-background px-2 py-1 text-sm text-foreground">
                {TRIGGER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t('triggers.action')}</label>
              <select value={trigger.action} onChange={(e) => updateTrigger(trigger.id, { action: e.target.value as TriggerRule['action'] })} className="w-full rounded border bg-background px-2 py-1 text-sm text-foreground">
                {TRIGGER_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t('triggers.delay')}</label>
              <input type="number" value={trigger.delay} onChange={(e) => updateTrigger(trigger.id, { delay: Number(e.target.value) })} className="w-full rounded border bg-background px-2 py-1 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t('triggers.config')}</label>
              <input type="text" placeholder="key=value" onChange={(e) => {
                const [k, v] = e.target.value.split('=')
                if (k) updateTrigger(trigger.id, { config: { ...trigger.config, [k.trim()]: v?.trim() } })
              }} className="w-full rounded border bg-background px-2 py-1 text-sm text-foreground" />
            </div>
            <button onClick={() => deleteTrigger(trigger.id)} className="rounded-md bg-destructive px-3 py-1.5 text-sm text-destructive-foreground">{t('triggers.delete')}</button>
          </div>
        ))}
      </div>
    </div>
  )
}
